import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FlaskConical, Loader2 } from 'lucide-react';
import { desactiverProduction } from '../services/api';
import { usePersonnalisation } from '../hooks/usePersonnalisation';

/**
 * Retour du site en mode essai. Écran DevOps, super administrateur seul.
 *
 * C'est le filet de sécurité d'un passage en production déclenché par
 * erreur, et la porte d'un environnement d'entraînement. Le retour ne
 * rétablit rien de ce que le passage a purgé : il rouvre seulement le mode —
 * et avec lui, le bouton qui purge. C'est précisément pour cela qu'il est
 * réservé à ce compte.
 */
export default function RetourEnEssai() {
  const queryClient = useQueryClient();
  const perso = usePersonnalisation();
  const [password, setPassword] = useState('');
  const [resultat, setResultat] = useState<{ ok: boolean; message: string } | null>(null);

  const desactiver = useMutation({
    mutationFn: () => desactiverProduction(password),
    onSuccess: (d: any) => {
      setResultat({ ok: true, message: d.message });
      setPassword('');
      queryClient.invalidateQueries({ queryKey: ['personnalisation'] });
      queryClient.invalidateQueries({ queryKey: ['admin-parametres-devops'] });
    },
    onError: (e: Error) => setResultat({ ok: false, message: e.message }),
  });

  const enProduction = perso?.modeEssai === false;

  return (
    <section className="card space-y-3">
      <h2 className="font-semibold text-gray-900 inline-flex items-center gap-2 dark:text-gray-100">
        <FlaskConical className="w-5 h-5 text-primary-700" /> Mode d'exploitation
      </h2>

      <p className="text-sm text-gray-600 dark:text-gray-400">
        Le site est{' '}
        <span className="font-semibold">
          {enProduction ? 'en production' : 'en mode essai'}
        </span>
        .{' '}
        {enProduction
          ? 'Le retour en essai rouvre le bouton de mise en production — et donc la purge — mais ne restaure rien de ce qui a été supprimé.'
          : 'Le passage en production se fait depuis l\'écran Paramètres, par un administrateur.'}
      </p>

      {enProduction && (
        <>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Votre mot de passe"
              autoComplete="current-password"
              className="input flex-1"
            />
            <button
              type="button"
              onClick={() => {
                setResultat(null);
                desactiver.mutate();
              }}
              disabled={desactiver.isPending || password.length === 0}
              className="btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {desactiver.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FlaskConical className="w-4 h-4" />
              )}
              Repasser en essai
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Le ruban « DEMO » réapparaît sur toutes les pages, et l'écran Paramètres
            propose de nouveau la mise en production.
          </p>
        </>
      )}

      {resultat && (
        <p
          className={`text-sm rounded-xl px-3 py-2 flex items-start gap-2 ${
            resultat.ok
              ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {resultat.ok ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          {resultat.message}
        </p>
      )}
    </section>
  );
}
